import type { ReactNode } from "react"
import { Landmark, RotateCw } from "lucide-react"

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuth } from "@/lib/auth"
import { nombreProvisional } from "@/lib/entities"
import { cn } from "@/lib/utils"
import { Link } from "react-router-dom"
import { useI18n, t } from "@/lib/i18n"

/**
 * Selector de SOCIEDAD, al lado del de cliente en la cabecera.
 *
 * Lo que esta pantalla tiene que dejar claro es de que sociedad son los numeros
 * que se estan mirando. En un producto contable, mirar la entidad equivocada es
 * un error caro y silencioso: los importes se ven plausibles y no hay nada que
 * chirrie. Por eso:
 *
 *  - la sociedad activa se ve SIEMPRE, tambien cuando hay una sola (no se
 *    esconde el dato solo porque no haya que elegir: se muestra sin destacar);
 *  - cuando no hay ninguna elegida y hay que elegir, el control se pinta con el
 *    color de aviso y lo pide con palabras, en vez de quedarse en un placeholder
 *    gris que se confunde con "vacio";
 *  - se muestra el identificador fiscal junto al nombre. Dos sociedades de un
 *    mismo grupo suelen llamarse casi igual; el RUC no.
 *
 * Mismo lenguaje visual que el selector de cliente: `Select` sin marco hasta
 * que se pasa por encima, altura de la barra, cifra en `.code`.
 */
export function EntitySelect() {
  const { t } = useI18n()
  const {
    activeTenantId,
    activeEntityId,
    setActiveEntityId,
    entities,
    entitiesLoading,
    entitiesError,
    refetchEntities,
  } = useAuth()

  if (!activeTenantId) return null

  if (entitiesLoading) {
    return (
      <Marco>
        <span className="text-2xs text-muted-foreground">{t("Buscando sociedades…")}</span>
      </Marco>
    )
  }

  if (entitiesError) {
    return (
      <Marco tono="alerta">
        <span className="text-2xs text-destructive">{t("No se pudieron listar las sociedades")}</span>
        <button
          type="button"
          onClick={refetchEntities}
          className="inline-flex items-center gap-1 rounded-sm px-1 text-2xs text-destructive underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <RotateCw className="h-3 w-3" />
          {t("Reintentar")}</button>
      </Marco>
    )
  }

  if (entities.length === 0) {
    return (
      <Marco
        tono="alerta"
        title={t("Este cliente no tiene ninguna sociedad visible para tu alcance. Sin sociedad no se pueden cargar contratos, personas ni centros de costo.")}
      >
        <span className="text-2xs text-muted-foreground">{t("Sin sociedades en tu alcance")}</span>
      </Marco>
    )
  }

  const faltaElegir = !activeEntityId

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <Landmark
        className={cn(
          "h-3.5 w-3.5 shrink-0",
          faltaElegir ? "text-warning" : "text-muted-foreground",
        )}
      />
      {/*
        EL RÓTULO, igual que el del cliente. Las dos coordenadas del alcance se
        veían idénticas —dos desplegables del mismo ancho— y nada decía cuál era
        cuál. Ver el comentario largo en `AppLayout`.
      */}
      <span
        className={cn(
          "eyebrow hidden shrink-0 lg:inline",
          faltaElegir ? "text-warning" : "text-muted-foreground",
        )}
      >
        {t("sociedad")}
      </span>
      <Select value={activeEntityId ?? undefined} onValueChange={setActiveEntityId}>
        <SelectTrigger
          className={cn(
            "h-7 w-[16rem] border-transparent bg-transparent font-medium shadow-none",
            "hover:border-input hover:bg-surface-sunken",
            faltaElegir &&
              "border-warning/60 bg-warning-soft text-warning-foreground hover:border-warning",
          )}
          aria-label={t("Sociedad activa")}
          /*
            EL ANCLA DE LAS PRUEBAS NO PUEDE SER EL `aria-label`, porque el
            `aria-label` se traduce —y debe traducirse: un lector de pantalla
            tiene que oírlo en el idioma de quien mira—. Con el idioma en
            «Automático», elegir la sociedad brasileña pasa la interfaz entera a
            portugués, así que el propio acto de usar este control cambia su
            nombre accesible y la prueba pierde el elemento a mitad de camino.
          */
          data-testid="selector-sociedad"
        >
          <SelectValue placeholder={t("Elige una sociedad")} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>
              {t("Las")}{" "}{entities.length}{" "}{t("sociedades de este cliente")}</SelectLabel>
            {/*
              TODAS LAS SOCIEDADES, NO UNA. Cesar: «si tiene más de una sociedad
              deberíamos poder ver todo, no sólo una por una». La parte que se
              puede resolver aquí es DECIR DÓNDE está ese «todo»: el consolidado
              del grupo vive en Holding, y las pantallas contables exigen una
              sociedad porque `entity_id` es obligatorio en `ledger` y `finance`.
              Convertirlas en multi-sociedad es cambiar esos endpoints Y decidir
              en qué moneda se suma —PEN, MXN, BRL y PAB no se suman—, que es
              precisamente lo que hace Holding. Está anotado en el traspaso para
              hablarlo antes de tocarlo.
            */}
            {entities.map((entidad) => (
              <SelectItem key={entidad.id} value={entidad.id}>
                <span className="flex items-baseline gap-2">
                  <span className="truncate">{entidad.name || nombreProvisional(entidad.id)}</span>
                  {entidad.taxId ? (
                    <span className="code shrink-0 text-2xs text-muted-foreground">
                      {entidad.taxId}
                    </span>
                  ) : null}
                  {entidad.country ? (
                    <span className="code shrink-0 text-2xs text-muted-foreground">
                      {entidad.country.toUpperCase()}
                    </span>
                  ) : null}
                </span>
              </SelectItem>
            ))}
          </SelectGroup>

          {/*
            DÓNDE ESTÁ «TODAS». No es una entrada seleccionable porque elegir
            «todas» aquí no significaría nada: las pantallas contables piden una
            sociedad y las cifras de cuatro monedas no se suman sin convertir.
            Lo que sí se puede hacer —y es lo que faltaba— es decir dónde vive
            esa vista en vez de dejar a alguien buscándola.
          */}
          {entities.length > 1 ? (
            <div className="border-t px-2 pb-1 pt-1.5">
              <p className="text-2xs leading-4 text-muted-foreground">
                {t("¿Las")}{" "}{entities.length}{" "}{t("a la vez? Eso es")}{" "}
                <Link
                  to="/holding/consolidado"
                  className="font-medium text-brand underline-offset-2 hover:underline"
                >
                  {t("Holding")}</Link>
                {t(": convierte las monedas, elimina lo que se facturan entre ellas y las suma como una sola.")}</p>
            </div>
          ) : null}
        </SelectContent>
      </Select>
    </div>
  )
}

/** Marco de los estados en los que no hay nada que elegir todavia. */
function Marco({
  children,
  tono,
  title,
}: {
  children: ReactNode
  tono?: "alerta"
  title?: string
}) {
  return (
    <div
      title={title}
      className={cn(
        "flex h-7 items-center gap-1.5 rounded-md border border-transparent px-1.5",
        tono === "alerta" && "border-warning/50 bg-warning-soft",
      )}
    >
      <Landmark
        className={cn(
          "h-3.5 w-3.5 shrink-0",
          tono === "alerta" ? "text-warning" : "text-muted-foreground",
        )}
      />
      {children}
    </div>
  )
}
